import { useEffect, useMemo, useState } from "react";
import { SortIcon } from "../feature-flags/SortIcon";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ComposedChart,
    Legend,
    Line,
    ResponsiveContainer,
    Scatter,
    ScatterChart,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import {
    apiService,
    type AnalyticsFilterParams,
    type AnalyticsStudentsResponse,
    type FirstPassResponse,
    type StudentsMetricsResponse,
} from "../../services/api.service";
import { Button } from "../ui/button";
import { AnalyticsFilters, type FilterDisplayState } from "./AnalyticsFilters";
import { AnalyticsHeatmap } from "./AnalyticsHeatmap";
import { ChartCard, MetricCard, SectionHeader, StatusBadge } from "./AnalyticsUI";
import { formatCount, formatMinutesFromMs, formatPercent, formatSummaryStat, mean, percentile } from "./analyticsFormatters";
import { ANALYTICS_COLORS } from "./analyticsTheme";

interface Props {
    filters: AnalyticsFilterParams;
    onFiltersChange: (f: AnalyticsFilterParams) => void;
    onStudentClick: (userId: number) => void;
    filterDisplay: FilterDisplayState;
    onFilterDisplayChange: (d: FilterDisplayState) => void;
}

type StudentView = StudentsMetricsResponse["students"][number] & {
    avg_exit_ticket_score: number | null;
    risk_score: number;
};

function completionTone(value: number): "success" | "warning" | "danger" {
    if (value >= 75) return "success";
    if (value >= 40) return "warning";
    return "danger";
}

export const AnalyticsStudents = ({ filters, onFiltersChange, onStudentClick, filterDisplay, onFilterDisplayChange }: Props) => {
    const [data, setData] = useState<StudentsMetricsResponse | null>(null);
    const [firstPass, setFirstPass] = useState<FirstPassResponse | null>(null);
    const [studentsData, setStudentsData] = useState<AnalyticsStudentsResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showHeatmap, setShowHeatmap] = useState(false);

    const loadData = async (params: AnalyticsFilterParams = {}) => {
        try {
            setIsLoading(true);
            setError(null);
            const [kidsResult, fpResult, studentsResult] = await Promise.all([
                apiService.getStudentsMetrics(params),
                apiService.getFirstPass(params),
                apiService.getAnalyticsStudents(params),
            ]);
            setData(kidsResult);
            setFirstPass(fpResult);
            setStudentsData(studentsResult);
        } catch {
            setError("Failed to load students metrics");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadData(filters);
    }, []);

    const handleFilterApply = (newFilters: AnalyticsFilterParams) => {
        onFiltersChange(newFilters);
        loadData(newFilters);
    };

    const enrichedStudents = useMemo<StudentView[]>(() => {
        if (!data) return [];
        const exitScores = new Map((studentsData?.students ?? []).map((student) => [student.user_id, student.avg_exit_ticket_score]));

        return data.students
            .map((student) => {
                const exitScore = exitScores.get(student.user_id) ?? null;
                const riskScore =
                    (100 - student.avg_completion_pct) * 0.35 +
                    (100 - student.accuracy_pct) * 0.25 +
                    Math.min(100, student.time_percentile) * 0.2 +
                    (exitScore == null ? 20 : (100 - exitScore) * 0.2);

                return {
                    ...student,
                    avg_exit_ticket_score: exitScore,
                    risk_score: Math.round(riskScore * 10) / 10,
                };
            })
            .sort((a, b) => b.risk_score - a.risk_score);
    }, [data, studentsData]);

    type StudentSortKey = keyof StudentView;
    const [studentSort, setStudentSort] = useState<{ key: StudentSortKey; dir: "asc" | "desc" }>({
        key: "risk_score",
        dir: "desc",
    });

    const handleStudentSort = (key: StudentSortKey) => {
        setStudentSort((prev) => ({
            key,
            dir: prev.key === key && prev.dir === "desc" ? "asc" : "desc",
        }));
    };

    const sortedStudents = useMemo<StudentView[]>(() => {
        return [...enrichedStudents].sort((a, b) => {
            const av = (a[studentSort.key] as number) ?? 0;
            const bv = (b[studentSort.key] as number) ?? 0;
            return studentSort.dir === "asc" ? av - bv : bv - av;
        });
    }, [enrichedStudents, studentSort]);

    const firstPassTimes = useMemo(() => {
        if (!firstPass) return [];
        const map = new Map(firstPass.first_pass_times.map((row) => [row.user_id, row.avg_first_pass_time_ms]));
        return enrichedStudents.slice(0, 8).map((student) => ({
            name: student.name || `User ${student.user_id}`,
            minutes: Math.round((map.get(student.user_id) ?? 0) / 60000),
        }));
    }, [firstPass, enrichedStudents]);

    const scatterData = useMemo(() => {
        return enrichedStudents.map((student) => ({
            x: student.scaffolding_pct ?? 0,
            y: student.accuracy_pct,
            z: Math.max(8, Math.round(student.avg_time_ms / 60000)),
            label: student.name || `User ${student.user_id}`,
            userId: student.user_id,
            risk: student.risk_score,
        }));
    }, [enrichedStudents]);

    const avgExitTicket = useMemo(() => {
        const exitScores = enrichedStudents
            .map((student) => student.avg_exit_ticket_score)
            .filter((score): score is number => score != null);
        if (!exitScores.length) return null;
        return exitScores.reduce((sum, value) => sum + value, 0) / exitScores.length;
    }, [enrichedStudents]);

    const completionSummary = useMemo(() => {
        const values = enrichedStudents.map((student) => student.avg_completion_pct);
        if (!values.length) return [];
        return [
            formatSummaryStat("mean", mean(values)),
            formatSummaryStat("p95", percentile(values, 95)),
        ];
    }, [enrichedStudents]);

    const accuracySummary = useMemo(() => {
        const values = enrichedStudents.map((student) => student.accuracy_pct);
        if (!values.length) return [];
        return [
            formatSummaryStat("mean", mean(values)),
            formatSummaryStat("p95", percentile(values, 95)),
        ];
    }, [enrichedStudents]);

    const exitSummary = useMemo(() => {
        const values = enrichedStudents
            .map((student) => student.avg_exit_ticket_score)
            .filter((score): score is number => score != null);
        if (!values.length) return [];
        return [
            formatSummaryStat("mean", mean(values)),
            formatSummaryStat("p95", percentile(values, 95)),
        ];
    }, [enrichedStudents]);

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                <p>Loading students metrics...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                <p>{error}</p>
                <Button variant="outline" className="mt-4" onClick={() => loadData(filters)}>Retry</Button>
            </div>
        );
    }

    if (!data) return null;

    const classAverage = data.class_averages;
    const atRiskCount = classAverage.at_risk_count ?? enrichedStudents.filter((student) => student.avg_completion_pct < 50).length;

    return (
        <div className="space-y-6">
            <SectionHeader
                title="Students"
                description="Intervention-first view of who is struggling, how far off they are, and which students should be reviewed first."
            />

            <AnalyticsFilters filters={filters} onApply={handleFilterApply} filterDisplay={filterDisplay} onFilterDisplayChange={onFilterDisplayChange} />

            <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
                <MetricCard
                    title="Students"
                    value={formatCount(enrichedStudents.length)}
                    hint="Students in the current cohort and filter window."
                    tone="neutral"
                />
                <MetricCard
                    title="At Risk"
                    value={formatCount(atRiskCount)}
                    hint="Students with completion below 50%."
                    tone={atRiskCount > 0 ? "danger" : "success"}
                />
                <MetricCard
                    title="On Track"
                    value={formatCount(classAverage.on_track_count ?? 0)}
                    hint="Students with avg completion ≥ 80%."
                    tone={(classAverage.on_track_count ?? 0) > 0 ? "success" : "neutral"}
                />
                <MetricCard
                    title="Stalled"
                    value={formatCount(classAverage.stalled_count ?? 0)}
                    hint="In-progress sessions with no activity in 7 days."
                    tone={(classAverage.stalled_count ?? 0) > 0 ? "warning" : "success"}
                />
                <MetricCard
                    title="Avg Completion"
                    value={formatPercent(classAverage.completion_pct)}
                    hint="Class average completion."
                    tone={completionTone(classAverage.completion_pct)}
                    summaries={completionSummary}
                />
                <MetricCard
                    title="Avg Accuracy"
                    value={formatPercent(classAverage.accuracy_pct)}
                    hint="Class average correctness."
                    tone={completionTone(classAverage.accuracy_pct)}
                    summaries={accuracySummary}
                />
                <MetricCard
                    title="Avg Exit Ticket"
                    value={avgExitTicket == null ? "-" : formatPercent(avgExitTicket)}
                    hint="Average exit-ticket score where available."
                    tone={avgExitTicket == null ? "neutral" : completionTone(avgExitTicket)}
                    summaries={exitSummary}
                />
                <MetricCard
                    title="Regressing"
                    value={formatCount(classAverage.regressing_count ?? 0)}
                    hint="Students whose recent completion is 10+ pts lower than earlier sessions."
                    tone={(classAverage.regressing_count ?? 0) > 0 ? "danger" : "success"}
                />
            </div>

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1.45fr_1fr]">
                <ChartCard
                    title="Intervention Rankings"
                    description="Sorted by a composite risk score using completion, accuracy, time burden, and exit-ticket signal."
                    contentClassName="p-0"
                >
                    {enrichedStudents.length === 0 ? (
                        <p className="py-12 text-center text-muted-foreground">No student data</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <div className="max-h-96 overflow-y-auto">
                            <table className="w-full text-sm">
                                <thead className="sticky top-0 z-10 bg-card">
                                    <tr className="border-b border-border bg-muted/50">
                                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Student</th>
                                        <th className="cursor-pointer select-none px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground" onClick={() => handleStudentSort("risk_score")}>
                                            Risk<SortIcon field={"risk_score" as never} currentField={studentSort.key as never} sortOrder={studentSort.dir} />
                                        </th>
                                        <th className="cursor-pointer select-none px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground" onClick={() => handleStudentSort("avg_completion_pct")}>
                                            Completion<SortIcon field={"avg_completion_pct" as never} currentField={studentSort.key as never} sortOrder={studentSort.dir} />
                                        </th>
                                        <th className="cursor-pointer select-none px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground" onClick={() => handleStudentSort("accuracy_pct")}>
                                            Accuracy<SortIcon field={"accuracy_pct" as never} currentField={studentSort.key as never} sortOrder={studentSort.dir} />
                                        </th>
                                        <th className="cursor-pointer select-none px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground" onClick={() => handleStudentSort("avg_time_ms")}>
                                            Avg Time<SortIcon field={"avg_time_ms" as never} currentField={studentSort.key as never} sortOrder={studentSort.dir} />
                                        </th>
                                        <th className="cursor-pointer select-none px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground" onClick={() => handleStudentSort("avg_exit_ticket_score")}>
                                            Exit Ticket<SortIcon field={"avg_exit_ticket_score" as never} currentField={studentSort.key as never} sortOrder={studentSort.dir} />
                                        </th>
                                        <th className="cursor-pointer select-none px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground" onClick={() => handleStudentSort("lessons_completed")}>
                                            Lessons<SortIcon field={"lessons_completed" as never} currentField={studentSort.key as never} sortOrder={studentSort.dir} />
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedStudents.map((student) => (
                                        <tr
                                            key={student.user_id}
                                            className="cursor-pointer border-b border-border/50 transition-colors hover:bg-muted/40"
                                            onClick={() => onStudentClick(student.user_id)}
                                        >
                                            <td className="px-4 py-3 font-medium">{student.name || `User ${student.user_id}`}</td>
                                            <td className="px-4 py-3 text-right">
                                                <StatusBadge
                                                    label={student.risk_score >= 60 ? "high" : student.risk_score >= 40 ? "watch" : "stable"}
                                                    tone={student.risk_score >= 60 ? "danger" : student.risk_score >= 40 ? "warning" : "success"}
                                                />
                                            </td>
                                            <td className="px-4 py-3 text-right font-medium">{formatPercent(student.avg_completion_pct)}</td>
                                            <td className="px-4 py-3 text-right">{formatPercent(student.accuracy_pct)}</td>
                                            <td className="px-4 py-3 text-right">{formatMinutesFromMs(student.avg_time_ms)}</td>
                                            <td className="px-4 py-3 text-right">
                                                {student.avg_exit_ticket_score == null ? "-" : formatPercent(student.avg_exit_ticket_score)}
                                            </td>
                                            <td className="px-4 py-3 text-right text-muted-foreground">
                                                {student.lessons_completed}/{student.lessons_started}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            </div>
                        </div>
                    )}
                </ChartCard>

                <ChartCard
                    title="Completion, Scaffolding & Risk Over Time"
                    description="Daily class averages: completion and scaffolding lines (left), at-risk student count bars (right)."
                >
                    {data.class_trend && data.class_trend.length > 0 ? (
                        <ResponsiveContainer width="100%" height={340}>
                            <ComposedChart data={data.class_trend}>
                                <CartesianGrid strokeDasharray="3 3" stroke={ANALYTICS_COLORS.grid} />
                                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                                <YAxis yAxisId="left" domain={[0, 100]} tick={{ fontSize: 11 }} />
                                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                                <Tooltip />
                                <Legend />
                                <Bar yAxisId="right" dataKey="at_risk_count" fill={ANALYTICS_COLORS.risk} fillOpacity={0.35} name="At-Risk Count" />
                                <Line yAxisId="left" type="monotone" dataKey="avg_completion" stroke={ANALYTICS_COLORS.completion} strokeWidth={2} dot={false} name="Avg Completion %" />
                                <Line yAxisId="left" type="monotone" dataKey="scaffolding_pct" stroke="#f97316" strokeWidth={2} dot={false} name="Scaffolding %" />
                            </ComposedChart>
                        </ResponsiveContainer>
                    ) : (
                        <p className="py-12 text-center text-muted-foreground">No class trend data</p>
                    )}
                </ChartCard>
            </div>

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
                <ChartCard
                    title="Most Scaffolding Needed"
                    description="Top 12 students by scaffolding percentage (medium + high support)."
                >
                    {enrichedStudents.length > 0 ? (
                        <ResponsiveContainer width="100%" height={340}>
                            <BarChart
                                data={[...enrichedStudents].sort((a, b) => (b.scaffolding_pct ?? 0) - (a.scaffolding_pct ?? 0)).slice(0, 12).map((s) => ({
                                    name: s.name || `User ${s.user_id}`,
                                    scaffolding_pct: s.scaffolding_pct ?? 0,
                                }))}
                                layout="vertical"
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke={ANALYTICS_COLORS.grid} />
                                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} />
                                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={120} />
                                <Tooltip formatter={(value?: number) => (value == null ? "-" : `${value}%`)} />
                                <Bar dataKey="scaffolding_pct" fill="#ef4444" radius={[0, 6, 6, 0]} name="Scaffolding %" />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <p className="py-12 text-center text-muted-foreground">No student data</p>
                    )}
                </ChartCard>

                <ChartCard
                    title="Lowest Accuracy"
                    description="Bottom 12 students by accuracy — worst first."
                >
                    {enrichedStudents.length > 0 ? (
                        <ResponsiveContainer width="100%" height={340}>
                            <BarChart
                                data={[...enrichedStudents].sort((a, b) => a.accuracy_pct - b.accuracy_pct).slice(0, 12).map((s) => ({
                                    name: s.name || `User ${s.user_id}`,
                                    accuracy_pct: s.accuracy_pct,
                                }))}
                                layout="vertical"
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke={ANALYTICS_COLORS.grid} />
                                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} />
                                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={120} />
                                <Tooltip formatter={(value?: number) => (value == null ? "-" : `${value}%`)} />
                                <Bar dataKey="accuracy_pct" fill={ANALYTICS_COLORS.warning} radius={[0, 6, 6, 0]} name="Accuracy %" />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <p className="py-12 text-center text-muted-foreground">No student data</p>
                    )}
                </ChartCard>

                <ChartCard
                    title="Lowest Exit Ticket Scores"
                    description="Bottom 8 students with exit ticket data — worst first."
                >
                    {enrichedStudents.filter((s) => s.avg_exit_ticket_score != null).length > 0 ? (
                        <ResponsiveContainer width="100%" height={340}>
                            <BarChart
                                data={[...enrichedStudents]
                                    .filter((s) => s.avg_exit_ticket_score != null)
                                    .sort((a, b) => (a.avg_exit_ticket_score ?? 100) - (b.avg_exit_ticket_score ?? 100))
                                    .slice(0, 8)
                                    .map((s) => ({
                                        name: s.name || `User ${s.user_id}`,
                                        exit_score: s.avg_exit_ticket_score ?? 0,
                                    }))}
                                layout="vertical"
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke={ANALYTICS_COLORS.grid} />
                                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} />
                                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={120} />
                                <Tooltip formatter={(value?: number) => (value == null ? "-" : `${value}%`)} />
                                <Bar dataKey="exit_score" fill={ANALYTICS_COLORS.info} radius={[0, 6, 6, 0]} name="Exit Ticket Score" />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <p className="py-12 text-center text-muted-foreground">No exit ticket data</p>
                    )}
                </ChartCard>
            </div>

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                <ChartCard
                    title="Scaffolding vs Accuracy"
                    description="Bubble size reflects average time. Students top-right have high scaffolding AND high accuracy — check bottom-right (low accuracy despite scaffolding)."
                >
                    {scatterData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={340}>
                            <ScatterChart>
                                <CartesianGrid strokeDasharray="3 3" stroke={ANALYTICS_COLORS.grid} />
                                <XAxis
                                    type="number"
                                    dataKey="x"
                                    domain={[0, 100]}
                                    tick={{ fontSize: 11 }}
                                    label={{ value: "Scaffolding %", position: "insideBottom", offset: -4, style: { fontSize: 11 } }}
                                />
                                <YAxis
                                    type="number"
                                    dataKey="y"
                                    domain={[0, 100]}
                                    tick={{ fontSize: 11 }}
                                    label={{ value: "Accuracy %", angle: -90, position: "insideLeft", style: { fontSize: 11 } }}
                                />
                                <Tooltip
                                    cursor={{ strokeDasharray: "3 3" }}
                                    content={({ active, payload }) => {
                                        if (!active || !payload?.length) return null;
                                        const row = payload[0].payload as { label: string; risk: number; x: number; y: number; z: number };
                                        return (
                                            <div className="rounded-lg border border-border bg-background p-3 text-sm shadow-lg">
                                                <p className="font-semibold">{row.label}</p>
                                                <p>Scaffolding: {formatPercent(row.x)}</p>
                                                <p>Accuracy: {formatPercent(row.y)}</p>
                                                <p>Time: {row.z}m</p>
                                                <p>Risk: {row.risk}</p>
                                            </div>
                                        );
                                    }}
                                />
                                <Scatter data={scatterData} dataKey="z">
                                    {scatterData.map((point) => (
                                        <Cell
                                            key={point.userId}
                                            fill={
                                                point.risk >= 60
                                                    ? ANALYTICS_COLORS.risk
                                                    : point.risk >= 40
                                                        ? ANALYTICS_COLORS.scaffoldingMed
                                                        : ANALYTICS_COLORS.completion
                                            }
                                        />
                                    ))}
                                </Scatter>
                            </ScatterChart>
                        </ResponsiveContainer>
                    ) : (
                        <p className="py-12 text-center text-muted-foreground">No student data</p>
                    )}
                </ChartCard>

                <ChartCard
                    title="First-Pass Time"
                    description="Useful where first-pass data exists today; this stays secondary to the main intervention ranking."
                >
                    {firstPassTimes.length > 0 ? (
                        <ResponsiveContainer width="100%" height={340}>
                            <BarChart data={firstPassTimes} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" stroke={ANALYTICS_COLORS.grid} />
                                <XAxis type="number" tick={{ fontSize: 11 }} />
                                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={130} />
                                <Tooltip formatter={(value?: number) => (value == null ? "-" : `${value}m`)} />
                                <Bar dataKey="minutes" fill={ANALYTICS_COLORS.info} radius={[0, 6, 6, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <p className="py-12 text-center text-muted-foreground">No first-pass data available.</p>
                    )}
                </ChartCard>
            </div>

            <div className="min-w-0">
                <ChartCard
                    title="Student x Lesson Completion"
                    description="Keep this as the fastest way to spot uneven coverage across the class."
                    contentClassName="overflow-x-auto p-0"
                    action={
                        <Button variant="ghost" size="sm" onClick={() => setShowHeatmap((value) => !value)}>
                            {showHeatmap ? "Hide" : "Show"}
                        </Button>
                    }
                >
                    {showHeatmap ? (
                        <AnalyticsHeatmap filters={filters} />
                    ) : (
                        <p className="py-12 text-center text-muted-foreground">Open the heatmap to inspect student-by-lesson completion patterns.</p>
                    )}
                </ChartCard>
            </div>
        </div>
    );
};
